/**
 * The orderbook ladder.
 *
 * **One book per market, and it is the UP book.** Per D1 the engine normalises
 * every order onto the UP leg, so this channel carries one ladder and the DOWN
 * ladder in the UI is a pure client-side transform: a bid of size `s` at price
 * `p` on the UP book is an ask of size `s` at `100 − p` on the DOWN book, with
 * the ladder reversed. It is implemented once, here, and never per component —
 * which halves book bandwidth and makes it structurally impossible for the two
 * ladders to disagree.
 *
 * **The ladder is not reconciled** (plan §2.3). Levels are mutated in place in
 * two sorted arrays; nothing here allocates a new object per delta. At 20 Hz
 * through the final three seconds, with 20–40 level changes per frame, the
 * allocation alone is the difference between 60 fps and a visible stutter on the
 * five seconds that matter.
 *
 * `size` is the **new absolute aggregate size at that level**, never a signed
 * delta, and `"0.000000"` deletes the level — it never means "a level with zero
 * size". Sizes are kept as the exact strings that arrived, because the checksum
 * is computed over the 6dp strings *exactly as sent* and re-formatting them
 * would make every verification fail.
 */

import { parseFixed, SHARES_DP } from '@/lib/decimal'
import { bookChecksum, checksumLevelCount, type ChecksumLevel } from '@/api/ws/checksum'
import { ExternalStore } from './store'

export interface BookLevelRow extends ChecksumLevel {
  price_cents: number
  size: string
  order_count?: number
}

export interface BookState {
  marketId: string | null
  /** Price-descending. `bids[0]` is the best bid. */
  bids: BookLevelRow[]
  /** Price-ascending. `asks[0]` is the best ask. */
  asks: BookLevelRow[]
  bestBidCents: number | null
  bestAskCents: number | null
  /** Levels per side this book was subscribed at, echoed by the server so the
   *  checksum covers the same `min(checksum_levels, depth)` on both ends. */
  depth: number | null
  /**
   * The book is known to be wrong or unconfirmed: a gap, a checksum mismatch, or
   * a dropped socket. Panels render the **last known good ladder behind a
   * `STALE` pill** and order entry against it is disabled — never a blank panel,
   * and never a book the client knows is wrong.
   */
  stale: boolean
  /** Snapshots applied since construction. A climbing count under steady load
   *  is the recovery loop failing to converge. */
  snapshots: number
  deltas: number
}

export interface BookSnapshotPayload {
  market_id?: string
  bids?: readonly BookLevelRow[]
  asks?: readonly BookLevelRow[]
  best_bid_cents?: number | null
  best_ask_cents?: number | null
  depth?: number
  checksum?: number
}

export interface BookChangePayload {
  book_side: 'BID' | 'ASK'
  price_cents: number
  size: string
}

export interface BookDeltaPayload {
  market_id?: string
  changes?: readonly BookChangePayload[]
  best_bid_cents?: number | null
  best_ask_cents?: number | null
  checksum?: number
}

function emptyState(): BookState {
  return {
    marketId: null,
    bids: [],
    asks: [],
    bestBidCents: null,
    bestAskCents: null,
    depth: null,
    stale: false,
    snapshots: 0,
    deltas: 0,
  }
}

export class BookStore {
  readonly store = new ExternalStore<BookState>(emptyState())

  get state(): BookState {
    return this.store.state
  }

  /**
   * Replace the ladder.
   *
   * Applies to both entry points of C1 — the in-band `snapshot` frame and the
   * REST `GET /markets/{id}/orderbook` body — because the two carry the same
   * shape by contract, and a client that parsed them differently would be two
   * clients (docs/04 §9.2).
   */
  applySnapshot(data: BookSnapshotPayload): void {
    const state = this.store.state
    state.marketId = data.market_id ?? state.marketId
    // Copied, not aliased: the frame's arrays belong to the buffer that is about
    // to be reused, and a ladder that aliases them mutates under the renderer.
    state.bids = (data.bids ?? []).map(cloneLevel)
    state.asks = (data.asks ?? []).map(cloneLevel)
    state.bestBidCents = data.best_bid_cents ?? state.bids[0]?.price_cents ?? null
    state.bestAskCents = data.best_ask_cents ?? state.asks[0]?.price_cents ?? null
    state.depth = data.depth ?? state.depth
    // **Staleness is not cleared here, and that is the whole of §F48's fix.**
    // A snapshot is data arriving; it is not evidence that the *stream* is
    // healthy. Gap recovery applies a REST snapshot through this method while
    // `Sequencer` is still `RECOVERING` and dropping every frame the socket
    // sends (`api/ws/client.ts`'s `#recover`), so a store that cleared the flag
    // here took the pill down over a ladder that was about to freeze — and the
    // order-entry gate reads that same flag.
    //
    // One owner: the sequencer says whether this channel is synced, through
    // `markStale`. Both routes back to `SYNCED` — an in-band snapshot frame and
    // a resubscribe `ack` — are transitions it observes, so nothing is lost.
    state.snapshots += 1
    this.store.touch()
  }

  /** Apply one coalesced 100 ms window of level changes. */
  applyDelta(data: BookDeltaPayload): void {
    const state = this.store.state
    if (data.market_id) state.marketId = data.market_id
    for (const change of data.changes ?? []) {
      const side = change.book_side === 'BID' ? state.bids : state.asks
      const descending = change.book_side === 'BID'
      if (isZeroSize(change.size)) removeLevel(side, change.price_cents, descending)
      else upsertLevel(side, change.price_cents, change.size, descending)
    }
    // Top-of-book is piggybacked on every delta so a client needing only BBO
    // never maintains a ladder. Trust it over the ladder's own head: they are
    // computed by the server at the same instant, and the ladder is truncated at
    // `depth` while the BBO is not.
    if (data.best_bid_cents !== undefined) state.bestBidCents = data.best_bid_cents
    if (data.best_ask_cents !== undefined) state.bestAskCents = data.best_ask_cents
    state.deltas += 1
    this.store.touch()
  }

  /**
   * CRC32 over the top `min(checksum_levels, depth)` levels per side.
   *
   * `checksum_levels` comes from `config.ws` — nothing in that object is a
   * constant in this client (plan §2.12) — and `depth` is echoed on the frame.
   */
  checksum(configuredLevels: number): number {
    const state = this.store.state
    const levels = checksumLevelCount(configuredLevels, state.depth ?? undefined)
    return bookChecksum(state.bids, state.asks, levels)
  }

  markStale(stale = true): void {
    if (this.store.state.stale === stale) return
    this.store.state.stale = stale
    this.store.touch()
  }

  /**
   * The DOWN ladder, derived.
   *
   * `bid s@p` becomes `ask s@(100−p)` and the ladder reverses, so DOWN bids come
   * from UP asks and vice versa. Both sides come out in their guaranteed order:
   * bids descending, asks ascending.
   *
   * **The mirror is the reversal**, and this reversed a second time until
   * 2026-09-22. `state.asks` is ascending, so mapping `100−p` over it is already
   * descending — the order a bid side is promised in — and the `.reverse()`
   * undid it. Nothing rendered the DOWN ladder, so the only witness was a store
   * test that pinned the inverted order; the day a panel passes
   * `outcome="DOWN"`, it would read the book upside down and the spread row
   * would print a negative.
   */
  downLadder(): { bids: BookLevelRow[]; asks: BookLevelRow[] } {
    const state = this.store.state
    return {
      bids: state.asks.map(mirrorLevel),
      asks: state.bids.map(mirrorLevel),
    }
  }

  /** Everything about this market is gone — the roll unsubscribed it, or a
   *  recovery is about to replace it wholesale. */
  reset(): void {
    this.store.set(emptyState())
  }
}

// ---------------------------------------------------------------------------

function cloneLevel(level: BookLevelRow): BookLevelRow {
  return level.order_count === undefined
    ? { price_cents: level.price_cents, size: level.size }
    : { price_cents: level.price_cents, size: level.size, order_count: level.order_count }
}

function mirrorLevel(level: BookLevelRow): BookLevelRow {
  return level.order_count === undefined
    ? { price_cents: 100 - level.price_cents, size: level.size }
    : { price_cents: 100 - level.price_cents, size: level.size, order_count: level.order_count }
}

/**
 * `"0.000000"` deletes, and the test has to be decimal-aware.
 *
 * `size === '0'`, `'0.0'` and `'0.000000'` are the same number and the server is
 * only promised to send one of them. A string compare against the 6dp spelling
 * would leave a phantom level on the ladder the first time it sent another.
 */
function isZeroSize(size: string): boolean {
  try {
    return parseFixed(size, SHARES_DP) === 0n
  } catch {
    // An unparseable size is not a deletion. Keeping the level and letting the
    // checksum fail is strictly better than silently removing liquidity.
    return false
  }
}

/**
 * Binary search for `price` in a sorted side.
 *
 * Returns the index if present, or `~insertionPoint` if not — the same
 * convention as every other binary search, so the two callers below read as
 * "found" and "where it goes".
 */
function findLevel(side: readonly BookLevelRow[], price: number, descending: boolean): number {
  let low = 0
  let high = side.length - 1
  while (low <= high) {
    const mid = (low + high) >>> 1
    const candidate = (side[mid] as BookLevelRow).price_cents
    if (candidate === price) return mid
    const goRight = descending ? candidate > price : candidate < price
    if (goRight) low = mid + 1
    else high = mid - 1
  }
  return ~low
}

function upsertLevel(side: BookLevelRow[], price: number, size: string, descending: boolean): void {
  const found = findLevel(side, price, descending)
  if (found >= 0) {
    // Mutate the existing row rather than replacing it: the ladder renders fixed
    // rows and a new object per touch is the reconciliation plan §2.3 bans.
    ;(side[found] as BookLevelRow).size = size
    return
  }
  side.splice(~found, 0, { price_cents: price, size })
}

function removeLevel(side: BookLevelRow[], price: number, descending: boolean): void {
  const found = findLevel(side, price, descending)
  if (found >= 0) side.splice(found, 1)
}
